/**
 * Minimal promise-based IndexedDB wrapper (requirement 2b: "IndexedDB for
 * DECODED large artifacts keyed by sha256 ... so a refresh restores the
 * brain instantly from IDB"). No dependency — three tiny object stores are
 * all this app needs, so a small hand-rolled wrapper beats pulling in a
 * library for it.
 *
 * IndexedDB natively structured-clones `ArrayBuffer`/typed arrays, so a
 * decoded `Float32Array` (layout positions, activity samples, ...) is
 * stored directly, no JSON round-trip.
 */

const DB_NAME = "observatory-cache";
const DB_VERSION = 1;

/** Decoded bulk artifacts, keyed by sha256 — the L2 cache beneath the
 * in-memory LRU (`src/cache/LruCache.ts`). */
export const STORE_ARTIFACTS = "artifacts";
/** Small mutable-file HTTP responses (index.json/latest.json/fleet.json),
 * keyed by URL, carrying the ETag needed for conditional revalidation. */
export const STORE_HTTP_CACHE = "http-cache";
/** Per-role bookkeeping (since-seq cursor, known event ids), keyed by role. */
export const STORE_META = "meta";

export interface StoredArtifact {
  readonly sha256: string;
  /** Which codec decoded `decoded` — a cheap sanity tag, not load-bearing
   * (the codec is already fixed by the artifact's own `ref.codec`).
   * `"json"` is used for cache-first-forever immutable JSON documents (v2
   * incremental event chunks, `src/cache/immutableCache.ts`) that aren't
   * one of the three binary artifact codecs but are equally immutable. */
  readonly kind: "npy" | "npz" | "activity" | "json";
  /** The *decoded* value (a parsed `NpyArray`, a `Record<string, NpyArray>`,
   * or an `ActivityGrid`) — structured-cloned directly, typed arrays and
   * all, so a cache hit skips both the network fetch and the CPU decode
   * (requirement 2b: "IndexedDB for DECODED large artifacts"). */
  readonly decoded: unknown;
  readonly storedAt: number;
}

export interface StoredHttpEntry {
  readonly url: string;
  readonly etag: string | null;
  readonly body: string;
  readonly storedAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB is not available in this environment"));
  }
  dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_ARTIFACTS)) {
        db.createObjectStore(STORE_ARTIFACTS, { keyPath: "sha256" });
      }
      if (!db.objectStoreNames.contains(STORE_HTTP_CACHE)) {
        db.createObjectStore(STORE_HTTP_CACHE, { keyPath: "url" });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed"));
  });
  return dbPromise;
}

function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

export async function idbGetArtifact(sha256: string): Promise<StoredArtifact | undefined> {
  const db = await openDb();
  const tx = db.transaction(STORE_ARTIFACTS, "readonly");
  const request = tx.objectStore(STORE_ARTIFACTS).get(sha256) as IDBRequest<
    StoredArtifact | undefined
  >;
  return promisifyRequest(request);
}

export async function idbPutArtifact(entry: StoredArtifact): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_ARTIFACTS, "readwrite");
  await promisifyRequest(tx.objectStore(STORE_ARTIFACTS).put(entry));
}

export async function idbGetHttpEntry(url: string): Promise<StoredHttpEntry | undefined> {
  const db = await openDb();
  const tx = db.transaction(STORE_HTTP_CACHE, "readonly");
  const request = tx.objectStore(STORE_HTTP_CACHE).get(url) as IDBRequest<
    StoredHttpEntry | undefined
  >;
  return promisifyRequest(request);
}

export async function idbPutHttpEntry(entry: StoredHttpEntry): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_HTTP_CACHE, "readwrite");
  await promisifyRequest(tx.objectStore(STORE_HTTP_CACHE).put(entry));
}

export async function idbGetMeta<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  const tx = db.transaction(STORE_META, "readonly");
  const request = tx.objectStore(STORE_META).get(key) as IDBRequest<T | undefined>;
  return promisifyRequest(request);
}

export async function idbSetMeta<T>(key: string, value: T): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_META, "readwrite");
  await promisifyRequest(tx.objectStore(STORE_META).put(value, key));
}

/** True when IndexedDB is usable in this environment (some privacy modes
 * disable it) — every caller falls back to "no L2 cache, network only"
 * rather than throwing when this is false. */
export function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

/** Test-only: drops the cached DB handle so a fresh `fake-indexeddb`
 * instance is opened on the next call (each vitest file gets its own). */
export function resetDbHandleForTests(): void {
  dbPromise = null;
}
